import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { CheckCircle2, DownloadCloud } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, type FieldValues, type Path, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ApiError } from '../api/client';
import { AppIcon } from '../components/AppIcon';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/display';
import { Checkbox, Field, Input, Textarea } from '../components/ui/inputs';
import { type RemoteSource, SearchableSelect } from '../components/ui/SearchableSelect';
import type { Lookups } from '../types/models';

export interface Option {
  value: string;
  label: string;
  hint?: string;
}

export type FormValues = Record<string, unknown>;

export interface FieldDef {
  name: string;
  label: string;
  type:
    | 'text'
    | 'password'
    | 'textarea'
    | 'select'
    | 'date'
    | 'decimal'
    | 'number'
    | 'checkbox'
    | 'image';
  required?: boolean;
  /** Fetch related values from an external source when this field gets a usable value. */
  lookup?: FieldLookup;
  options?:
    | Option[]
    | ((lookups: Lookups | undefined, current: FormValues | undefined) => Option[]);
  /**
   * Searchable server-side source for a select (AJAX). A function receives the
   * current form values so the list can depend on other fields.
   */
  remote?: RemoteSource | ((values: FormValues) => RemoteSource);
  /** Placeholder of the select (also what an empty optional select means). */
  emptyLabel?: string;
  /** Show the field only when this returns true for the current values. */
  showIf?: (values: FormValues) => boolean;
  placeholder?: string;
  help?: string;
  /** Spans the full form width. */
  wide?: boolean;
  /** Shown read-only when editing (identifiers that should not change). */
  readOnlyOnEdit?: boolean;
  /** Fill this field from another field's value while it is still empty. */
  autofill?: { from: string; compute: (value: string) => string | null | undefined };
  /** Only shown when editing (e.g. status on create defaults to ACTIVE). */
  editOnly?: boolean;
}

export interface FieldLookup {
  buttonLabel: string;
  /** Whether the current value is complete enough to look up. */
  ready: (value: string) => boolean;
  /** Returns the values to set on the form and a short confirmation message. */
  run: (value: string, current: FormValues) => Promise<{ values: FormValues; message: string }>;
}

type LookupState = { status: 'idle' | 'loading' } | { status: 'done' | 'error'; message: string };

/**
 * Text input that runs `lookup` automatically shortly after a usable value is
 * typed or pasted, and on demand via its button.
 */
function LookupInput({
  field,
  id,
  invalid,
  initialValue,
  form,
}: {
  field: FieldDef;
  id: string;
  invalid: boolean;
  initialValue: string;
  form: ReturnType<typeof useForm<FieldValues>>;
}) {
  const lookup = field.lookup!;
  const [state, setState] = useState<LookupState>({ status: 'idle' });
  // Do not auto-run for the value the form opened with (editing an existing record).
  const lastLookedUp = useRef(initialValue);
  const value = String(form.watch(field.name) ?? '').trim();

  const run = useCallback(
    async (input: string) => {
      lastLookedUp.current = input;
      setState({ status: 'loading' });
      try {
        const result = await lookup.run(input, form.getValues());
        for (const [k, v] of Object.entries(result.values)) {
          form.setValue(k, v, { shouldDirty: true, shouldValidate: true });
        }
        lastLookedUp.current = String(form.getValues(field.name) ?? '').trim();
        setState({ status: 'done', message: result.message });
      } catch (err) {
        setState({
          status: 'error',
          message: err instanceof ApiError ? err.message : 'Lookup failed',
        });
      }
    },
    [lookup, form, field.name],
  );

  useEffect(() => {
    if (!value || value === lastLookedUp.current || !lookup.ready(value)) return;
    const t = setTimeout(() => void run(value), 600);
    return () => clearTimeout(t);
  }, [value, lookup, run]);

  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <Input
          id={id}
          invalid={invalid}
          placeholder={field.placeholder}
          {...form.register(field.name)}
        />
        <Button
          variant="secondary"
          className="shrink-0"
          loading={state.status === 'loading'}
          disabled={!lookup.ready(value)}
          icon={<DownloadCloud className="size-4" />}
          onClick={() => void run(value)}
        >
          {lookup.buttonLabel}
        </Button>
      </div>
      {state.status === 'done' && (
        <p className="flex items-center gap-1.5 text-xs text-emerald-700" role="status">
          <CheckCircle2 className="size-3.5" /> {state.message}
        </p>
      )}
      {state.status === 'error' && (
        <p className="text-xs text-rose-600" role="alert">
          {state.message}
        </p>
      )}
    </div>
  );
}

/**
 * Generic form for master data. Validates with the same Zod schema as the
 * API and maps server-side field errors (e.g. duplicate GSTIN) onto fields.
 */
export function ResourceForm({
  formId,
  fields,
  schema,
  defaultValues,
  isEdit,
  lookups,
  onSubmit,
}: {
  formId: string;
  fields: FieldDef[];
  schema: z.ZodType;
  defaultValues: FormValues;
  isEdit: boolean;
  lookups: Lookups | undefined;
  onSubmit: (values: FormValues) => Promise<void>;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<FieldValues>({
    // The schema's output type differs from raw input (strings → numbers / null).
    resolver: zodResolver(schema as never) as never,
    defaultValues,
  });
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = form;

  // Autofill dependent fields (e.g. state from GSTIN).
  useEffect(() => {
    const autofills = fields.filter((f) => f.autofill);
    if (!autofills.length) return;
    const sub = watch((values, { name }) => {
      for (const f of autofills) {
        if (name !== f.autofill!.from) continue;
        const computed = f.autofill!.compute(String(values[f.autofill!.from] ?? ''));
        if (computed && !getValues(f.name)) setValue(f.name, computed, { shouldValidate: true });
      }
    });
    return () => sub.unsubscribe();
  }, [fields, watch, getValues, setValue]);

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      if (err instanceof ApiError) {
        const known = new Set(fields.map((f) => f.name));
        let mapped = false;
        for (const e of err.errors) {
          if (known.has(e.path)) {
            setError(e.path as Path<FieldValues>, { message: e.message });
            mapped = true;
          }
        }
        if (!mapped || err.errors.length === 0) setFormError(err.message);
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    }
  });

  const current = isEdit ? defaultValues : undefined;
  const values = watch();

  return (
    <form id={formId} onSubmit={submit} noValidate className="space-y-4">
      {formError && <Alert tone="error">{formError}</Alert>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields
          .filter((f) => (!f.editOnly || isEdit) && (!f.showIf || f.showIf(values)))
          .map((f) => {
            const error = errors[f.name]?.message as string | undefined;
            const id = `${formId}-${f.name}`;
            const readOnly = isEdit && f.readOnlyOnEdit;
            const options =
              typeof f.options === 'function' ? f.options(lookups, current) : (f.options ?? []);

            let control;
            if (f.lookup) {
              control = (
                <LookupInput
                  field={f}
                  id={id}
                  invalid={!!error}
                  initialValue={String(defaultValues[f.name] ?? '').trim()}
                  form={form}
                />
              );
            } else if (f.type === 'image') {
              const url = String(watch(f.name) ?? '');
              control = (
                <div className="flex items-center gap-3">
                  <AppIcon src={url} name={String(watch('name') ?? '')} size={40} />
                  <Input
                    id={id}
                    invalid={!!error}
                    placeholder={f.placeholder}
                    {...register(f.name)}
                  />
                </div>
              );
            } else
              switch (f.type) {
                case 'textarea':
                  control = (
                    <Textarea
                      id={id}
                      invalid={!!error}
                      placeholder={f.placeholder}
                      {...register(f.name)}
                    />
                  );
                  break;
                case 'select': {
                  const remote = typeof f.remote === 'function' ? f.remote(values) : f.remote;
                  control = (
                    <Controller
                      control={form.control}
                      name={f.name}
                      render={({ field }) => (
                        <SearchableSelect
                          inputId={id}
                          value={field.value == null ? '' : String(field.value)}
                          onChange={(v) => field.onChange(v)}
                          options={remote ? undefined : options}
                          remote={remote}
                          invalid={!!error}
                          clearable={!f.required}
                          placeholder={f.emptyLabel ?? (f.required ? 'Select…' : '— None —')}
                        />
                      )}
                    />
                  );
                  break;
                }
                case 'checkbox':
                  control = <Checkbox label={f.help ?? f.label} {...register(f.name)} />;
                  break;
                default:
                  control = (
                    <Input
                      id={id}
                      type={
                        f.type === 'date' ? 'date' : f.type === 'password' ? 'password' : 'text'
                      }
                      autoComplete={f.type === 'password' ? 'new-password' : undefined}
                      inputMode={
                        f.type === 'decimal'
                          ? 'decimal'
                          : f.type === 'number'
                            ? 'numeric'
                            : undefined
                      }
                      invalid={!!error}
                      placeholder={f.placeholder}
                      readOnly={readOnly}
                      className={clsx(
                        readOnly && 'bg-slate-100 text-slate-500',
                        f.type === 'decimal' && 'num',
                      )}
                      {...register(f.name)}
                    />
                  );
              }

            return (
              <Field
                key={f.name}
                label={f.type === 'checkbox' ? '' : f.label}
                htmlFor={id}
                error={error}
                help={f.type === 'checkbox' ? undefined : f.help}
                required={f.required}
                className={clsx(f.wide && 'sm:col-span-2')}
              >
                {control}
              </Field>
            );
          })}
      </div>
    </form>
  );
}
